import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchMeeting, mediaUrl } from './api';
import type { Meeting, TranscriptLine } from './api';
import { Markdown } from './Markdown';
import type { Route } from './routes';

/**
 * A recorded meeting, played back like a video (MN-35).
 *
 * THERE IS NO VIDEO FILE. The recorder writes one file per person per device —
 * that is the whole point of it — so "the recording" is a set of tracks, each
 * starting at its own moment on the call's clock (`offset`) and lasting as long
 * as that person was there. This component composes them at play time: one
 * master clock, and every track a follower that is shown, seeked and played
 * only while the clock is inside its span. Nothing is mixed or re-encoded on the
 * server; the browser plays the original streams, remuxed to WebM (MN-34).
 *
 * The clock is the MEETING's, in seconds since it started — the same clock a
 * transcript line's `t` is on — so a line, a seek and a track all agree.
 */

/** A track the player can schedule. */
interface PlayTrack {
  id: string;
  name: string;
  kind: 'audio' | 'video';
  source: string;
  url: string;
  /** Seconds into the meeting it starts. */
  offset: number;
  /** Seconds long, once known (from the server, else from the element). */
  duration?: number;
}

/** A follower further than this from where it should be is seeked back into line. */
const DRIFT_S = 0.35;
const SPEEDS = [1, 1.25, 1.5, 2];
/** The longest the clock waits on a track that is still loading. */
const MAX_HOLD_S = 4;

function clock(s: number): string {
  const t = Math.max(0, Math.floor(s));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  const mm = String(m).padStart(h ? 2 : 1, '0');
  return `${h ? `${h}:` : ''}${mm}:${String(sec).padStart(2, '0')}`;
}

function tracksOf(m: Meeting): PlayTrack[] {
  const out: PlayTrack[] = [];
  for (const p of m.participants) {
    for (const t of p.tracks ?? []) {
      if (t.erased) continue;
      out.push({
        id: t.file,
        name: p.name || p.principal,
        kind: t.kind,
        source: t.source,
        // The server's browser-playable URL when it offers one (a WebM remux);
        // otherwise the original file, which plays where the browser can.
        url: t.play ?? mediaUrl(m.room, m.session, t.file),
        offset: t.offset ?? 0,
        duration: t.duration,
      });
    }
  }
  return out;
}

/** The meeting's length: its recorded end, else the end of its longest track. */
function lengthOf(m: Meeting, tracks: PlayTrack[], known: Record<string, number>): number {
  let end = 0;
  if (m.endedAt) end = (Date.parse(m.endedAt) - Date.parse(m.startedAt)) / 1000;
  for (const t of tracks) {
    const d = known[t.id] ?? t.duration;
    if (d) end = Math.max(end, t.offset + d);
  }
  return Math.max(end, 0);
}

interface PlayerProps {
  room: string;
  session: string;
  token?: string;
  at?: number;
  navigate: (r: Route) => void;
}

export function Player({ room, session, token, at, navigate }: PlayerProps) {
  const [m, setM] = useState<Meeting | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchMeeting(room, session, token)
      .then((x) => live && setM(x))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [room, session, token]);

  if (error) return <p className="mx-error">{error}</p>;
  if (!m) return <p className="mx-muted">Loading the recording…</p>;
  if (m.playable === false) {
    return (
      <p className="mx-muted">
        Only the people who were in this meeting can play it back.
      </p>
    );
  }
  return <PlayerView m={m} at={at} navigate={navigate} token={token} />;
}

function PlayerView({
  m,
  at,
  navigate,
  token,
}: {
  m: Meeting;
  at?: number;
  navigate: (r: Route) => void;
  token?: string;
}) {
  const tracks = useMemo(() => tracksOf(m), [m]);
  const lines: TranscriptLine[] = useMemo(() => m.transcript?.lines ?? [], [m]);

  // Durations the server did not send, learned from each element's metadata.
  const [known, setKnown] = useState<Record<string, number>>({});
  const length = lengthOf(m, tracks, known);

  // THE MASTER CLOCK. A ref, advanced by requestAnimationFrame from the wall
  // clock while playing; React state mirrors it a few times a second for the
  // UI. Driving it from wall time rather than from one media element is what
  // lets it run through the stretches where nobody's track covers the clock.
  const tRef = useRef(at ?? 0);
  const [t, setT] = useState(at ?? 0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [muted, setMuted] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [tab, setTab] = useState<'transcript' | 'summary'>('transcript');

  const els = useRef(new Map<string, HTMLMediaElement>());
  const root = useRef<HTMLDivElement | null>(null);

  const active = useCallback(
    (tr: PlayTrack, now: number) => {
      // THE FILE'S OWN LENGTH WINS once the element knows it: it is what can
      // actually be played. The server's figure is only for before that, and
      // it has been wrong in both directions — a wall-clock span seconds too
      // long, and a probe of an IVF header that read as 0.02 s (MN-34).
      const d = known[tr.id] ?? tr.duration;
      const local = now - tr.offset;
      return local >= 0 && (d === undefined || local < d);
    },
    [known],
  );

  /** Bring every follower into line with the clock: play, pause, seek, rate. */
  const sync = useCallback(
    (now: number, hard: boolean) => {
      let waiting = false;
      for (const tr of tracks) {
        const el = els.current.get(tr.id);
        if (!el) continue;
        if (!active(tr, now)) {
          if (!el.paused) el.pause();
          continue;
        }
        const local = now - tr.offset;
        // THE MEDIA MAY END BEFORE THE MANIFEST SAYS. The manifest's span is
        // the recorder's wall clock; the file holds what was actually written,
        // which on a real hour-long call came out ~3 s shorter. Past the file's
        // own end the track is DONE: paused and left alone. Seeking it again
        // every frame would restart a seek that can only clamp to the end, and
        // a seeking element reads as loading, which held the clock.
        const own = el.duration;
        if (Number.isFinite(own) && local >= own - 0.1) {
          if (!el.paused) el.pause();
          continue;
        }
        if (hard || Math.abs(el.currentTime - local) > DRIFT_S) el.currentTime = local;
        el.playbackRate = rate;
        el.muted = muted;
        if (playing) {
          if (el.paused && !el.ended) void el.play().catch(() => {});
          // Loading, as opposed to finished: at the tail of a file there IS no
          // future data, so readyState stays below HAVE_FUTURE_DATA for good,
          // and holding the clock on that froze playback at the end of every
          // track. Only a track with more than a moment left can make us wait.
          const d = known[tr.id] ?? tr.duration ?? el.duration;
          const left = Number.isFinite(d) ? d - local : Infinity;
          if (el.readyState < 3 && !el.ended && left > 0.5) waiting = true;
        } else if (!el.paused) {
          el.pause();
        }
      }
      setBuffering(waiting);
      return waiting;
    },
    [tracks, active, known, rate, muted, playing],
  );

  // The clock loop.
  useEffect(() => {
    if (!playing) {
      sync(tRef.current, false);
      return;
    }
    let raf = 0;
    let last = performance.now();
    let lastUi = 0;
    let heldFor = 0;
    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      // Hold the clock while an active track is still loading, so nothing
      // runs ahead of the picture somebody is waiting for — but never for more
      // than a few seconds: one track that will not load must not stop the
      // whole meeting from playing.
      const waiting = sync(tRef.current, false);
      if (waiting) heldFor += dt;
      else heldFor = 0;
      if (!waiting || heldFor > MAX_HOLD_S) tRef.current = Math.min(length || Infinity, tRef.current + dt * rate);
      if (length && tRef.current >= length) {
        setPlaying(false);
      }
      if (now - lastUi > 200) {
        lastUi = now;
        setT(tRef.current);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, rate, length, sync]);

  const seek = useCallback(
    (to: number) => {
      const v = Math.max(0, Math.min(length || to, to));
      tRef.current = v;
      setT(v);
      sync(v, true);
    },
    [length, sync],
  );

  const toggle = useCallback(() => {
    if (!playing && length && tRef.current >= length - 0.1) seek(0);
    if (!playing) {
      // INSIDE THE TAP. iOS Safari lets unmuted audio start only in a user
      // gesture, and the clock loop calls play() from requestAnimationFrame,
      // which is not one; so a phone would play the meeting silent, and a
      // late joiner's audio could never start. Start what is due now, and
      // prime every other audio element (play then pause) so a later play()
      // from the loop is allowed.
      const now = tRef.current;
      for (const tr of tracks) {
        const el = els.current.get(tr.id);
        if (!el || tr.kind !== 'audio') continue;
        if (active(tr, now)) {
          el.currentTime = now - tr.offset;
          void el.play().catch(() => {});
        } else {
          void el
            .play()
            .then(() => el.pause())
            .catch(() => {});
        }
      }
    }
    setPlaying((p) => !p);
  }, [playing, length, seek, tracks, active]);

  const fullscreen = useCallback(() => {
    const el = root.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen?.();
  }, []);

  const cycleRate = useCallback((dir: 1 | -1) => {
    setRate((r) => {
      const i = SPEEDS.indexOf(r);
      return SPEEDS[Math.max(0, Math.min(SPEEDS.length - 1, (i < 0 ? 0 : i) + dir))] ?? 1;
    });
  }, []);

  // Keyboard, as on any video site. Ignored while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          toggle();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seek(tRef.current - 5);
          break;
        case 'ArrowRight':
          e.preventDefault();
          seek(tRef.current + 5);
          break;
        case 'j':
          seek(tRef.current - 10);
          break;
        case 'l':
          seek(tRef.current + 10);
          break;
        case 'f':
          fullscreen();
          break;
        case 'm':
          setMuted((x) => !x);
          break;
        case '>':
          cycleRate(1);
          break;
        case '<':
          cycleRate(-1);
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle, seek, fullscreen, cycleRate]);

  // What is on screen at time t.
  const videos = tracks.filter((x) => x.kind === 'video');
  const audios = tracks.filter((x) => x.kind === 'audio');
  const screens = videos.filter((x) => x.source === 'screen_share' && active(x, t));
  const cameras = videos.filter((x) => x.source !== 'screen_share' && active(x, t));
  const main = screens[0];

  const current = useMemo(() => {
    let i = -1;
    for (let k = 0; k < lines.length; k++) {
      if ((lines[k]?.t ?? Infinity) <= t) i = k;
      else break;
    }
    return i;
  }, [lines, t]);

  const register = (tr: PlayTrack) => (el: HTMLMediaElement | null) => {
    if (el) els.current.set(tr.id, el);
    else els.current.delete(tr.id);
  };
  const learn = (tr: PlayTrack) => (e: React.SyntheticEvent<HTMLMediaElement>) => {
    const d = e.currentTarget.duration;
    if (Number.isFinite(d) && d > 0) {
      setKnown((k) => (k[tr.id] === d ? k : { ...k, [tr.id]: d }));
    }
  };

  const title = m.title || m.room;
  const started = new Date(m.startedAt);

  return (
    <div className="mx-pb" ref={root}>
      <div className="mx-pb-main">
        <div className="mx-pb-head">
          <button
            className="mx-invite-btn"
            onClick={() => navigate({ name: 'meeting', room: m.room, session: m.session, token })}
          >
            ← Meeting
          </button>
          <span className="mx-pb-title">{title}</span>
          <span className="mx-muted">{started.toLocaleString()}</span>
        </div>

        <div
          className="mx-pb-stage"
          data-layout={main ? 'focus' : cameras.length <= 1 ? 'solo' : 'grid'}
          onClick={toggle}
        >
          {videos.map((tr) => {
            const on = active(tr, t);
            const isMain = main?.id === tr.id;
            const role = isMain ? 'main' : tr.source === 'screen_share' ? 'off' : main ? 'side' : 'tile';
            return (
              <figure
                key={tr.id}
                className="mx-pb-tile"
                data-role={on ? role : 'off'}
                data-source={tr.source}
              >
                <video
                  ref={register(tr)}
                  src={tr.url}
                  preload="metadata"
                  playsInline
                  muted
                  onLoadedMetadata={learn(tr)}
                />
                <figcaption>
                  {tr.name}
                  {tr.source === 'screen_share' ? ' · screen' : ''}
                </figcaption>
              </figure>
            );
          })}
          {cameras.length === 0 && !main && (
            <div className="mx-pb-empty">{audios.some((a) => active(a, t)) ? 'Audio only' : 'Nobody on camera here'}</div>
          )}
          {buffering && playing && <div className="mx-pb-buffering">Loading…</div>}
          {!playing && (
            <div className="mx-pb-big" aria-hidden="true">
              ▶
            </div>
          )}
        </div>

        {audios.map((tr) => (
          <audio key={tr.id} ref={register(tr)} src={tr.url} preload="metadata" onLoadedMetadata={learn(tr)} />
        ))}

        <div className="mx-pb-controls">
          <div className="mx-pb-seek">
            <div className="mx-pb-marks" aria-hidden="true">
              {length > 0 &&
                lines.map((l, i) => (
                  <i key={i} style={{ left: `${(l.t / length) * 100}%` }} />
                ))}
            </div>
            <input
              type="range"
              min={0}
              max={length || 0}
              step={0.1}
              value={Math.min(t, length || t)}
              onChange={(e) => seek(Number(e.currentTarget.value))}
              aria-label="Seek"
            />
          </div>
          <div className="mx-pb-row">
            <button className="mx-pb-btn" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? '❚❚' : '▶'}
            </button>
            <button className="mx-pb-btn" onClick={() => seek(tRef.current - 10)} aria-label="Back 10 seconds">
              −10
            </button>
            <button className="mx-pb-btn" onClick={() => seek(tRef.current + 10)} aria-label="Forward 10 seconds">
              +10
            </button>
            <span className="mx-pb-time">
              {clock(t)} / {clock(length)}
            </span>
            <span className="mx-spacer" />
            <button className="mx-pb-btn" onClick={() => setMuted((x) => !x)} aria-pressed={muted}>
              {muted ? 'Unmute' : 'Mute'}
            </button>
            <button className="mx-pb-btn" onClick={() => cycleRate(rate >= 2 ? -1 : 1)} title="Speed (< and >)">
              {rate}×
            </button>
            <button className="mx-pb-btn" onClick={fullscreen} aria-label="Fullscreen">
              ⛶
            </button>
          </div>
        </div>
      </div>

      <aside className="mx-pb-side">
        <div className="mx-pb-tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'transcript'} onClick={() => setTab('transcript')}>
            Transcript
          </button>
          <button role="tab" aria-selected={tab === 'summary'} onClick={() => setTab('summary')}>
            Summary
          </button>
        </div>
        {tab === 'summary' ? (
          <div className="mx-pb-summary">
            {m.summary ? <Markdown text={m.summary} /> : <p className="mx-muted">No summary yet.</p>}
          </div>
        ) : (
          <TranscriptPane lines={lines} current={current} onSeek={(s) => seek(s)} />
        )}
      </aside>
    </div>
  );
}

/**
 * The transcript beside the picture: the line being said is lit, a click on any
 * line seeks there, and the pane follows along — by its OWN scrollTop, never
 * scrollIntoView, which would drag the page with it (MN-13).
 */
function TranscriptPane({
  lines,
  current,
  onSeek,
}: {
  lines: TranscriptLine[];
  current: number;
  onSeek: (t: number) => void;
}) {
  const box = useRef<HTMLOListElement | null>(null);
  useEffect(() => {
    const el = box.current;
    const li = el?.children[current] as HTMLElement | undefined;
    if (!el || !li) return;
    const top = li.offsetTop - el.clientHeight / 3;
    if (Math.abs(el.scrollTop - top) > el.clientHeight / 3) el.scrollTop = top;
  }, [current]);

  if (lines.length === 0) return <p className="mx-muted mx-pb-pad">No transcript for this meeting.</p>;
  return (
    <ol className="mx-pb-tx" ref={box}>
      {lines.map((l, i) => (
        <li key={i} data-current={i === current ? 'true' : undefined}>
          <button onClick={() => onSeek(l.t)}>
            <span className="mx-pb-tx-at">{clock(l.t)}</span>
            <span className="mx-pb-tx-who">{l.speaker}</span>
            <span className="mx-pb-tx-text">{l.text}</span>
          </button>
        </li>
      ))}
    </ol>
  );
}
